import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { useAgent } from '~/contexts/AgentContext';
import { GradientText } from '~/eds/src/components/GradientText/GradientText';
import { Icon } from '~/eds/src/components/Icon/Icon';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useCurrentUser } from '~/hooks/useCurrentUser';

import { AGENT_IDS } from '../chat-bot-panel/agentIds';
import { useWorkflowRoutingEnabled } from '../chat-bot-panel/hooks/useWorkflowRoutingEnabled';

export const NewConversation = () => {
  const agentContext = useAgent();
  const isWorkflowRoutingEnabled = useWorkflowRoutingEnabled();
  const hasWorkflowRoutingAgent = useFlag(FlagType.WorkflowRoutingAgent);

  const onSelectWorkflowRouting = useCallback(() => {
    agentContext?.selectAgent(AGENT_IDS.WORKFLOW_ROUTING);
  }, [agentContext]);
  const intl = useIntl();
  const currentUser = useCurrentUser();
  return (
    <Layout w="100%" h="100%" align="center">
      <Layout direction="column" w="100%" maxW="680px" px={6}>
        <Icon name="evisort-ai" icon="ai-filled" size="m" mb={3} />
        <GradientText
          fontWeight="medium"
          fromColor="brand"
          toColor="brand"
          toColorOpacity={40}
          variant="title"
        >
          {intl.formatMessage(
            {
              id: 'NewConversation.greeting',
              defaultMessage: 'Hi {name}',
              description: 'Greeting text for new conversation',
            },
            {
              name: currentUser.firstName,
            },
          )}
        </GradientText>
        <Text
          variant="title"
          color="text.quiet"
          fontWeight="medium"
          {...(hasWorkflowRoutingAgent && { mt: 2 })}
        >
          {hasWorkflowRoutingAgent
            ? intl.formatMessage({
                id: 'NewConversation.helpMessage',
                defaultMessage: 'How can I help you?',
                description: 'Help message for new conversation',
              })
            : intl.formatMessage({
                id: 'NewConversation.askAboutYourFiles',
                defaultMessage: 'Ask about your files',
                description: 'Text prompting user to ask AI about their files',
              })}
        </Text>

        {isWorkflowRoutingEnabled && agentContext && (
          <>
            <Layout my={6}></Layout>

            <Text variant="body" color="text.quiet" fontWeight="medium" mb={3}>
              {intl.formatMessage({
                id: 'NewConversation.agentSuggested',
                defaultMessage: 'Agent Suggested',
                description: 'Label for suggested agent section',
              })}
            </Text>

            <Layout
              direction="row"
              align="flex-start"
              spacing={3}
              p={4}
              borderRadius="m"
              bg="surface.secondary"
              borderColor="border.secondary"
              cursor="pointer"
              onClick={onSelectWorkflowRouting}
              data-testid="select-workflow-routing-agent"
              _hover={{
                bg: 'surface.tertiary',
              }}
            >
              <Layout
                align="center"
                justify="center"
                bg="status.active.secondary"
                borderRadius="50%"
                w="32px"
                h="32px"
                flex="none"
              >
                <Icon
                  icon="workflow-routing-agent"
                  size="s"
                  color="status.active.secondary"
                />
              </Layout>
              <Layout
                direction="column"
                spacing={1}
                flex={1}
                align="flex-start"
              >
                <Text variant="body" fontWeight="medium" color="text.primary">
                  {intl.formatMessage({
                    id: 'NewConversation.workflowRouting',
                    defaultMessage: 'Workflow Routing',
                    description: 'Label for workflow routing agent',
                  })}
                </Text>
                <Text variant="body" color="text.secondary">
                  {intl.formatMessage({
                    id: 'NewConversation.workflowRoutingDescription',
                    defaultMessage: 'Ask AI to route to a workflow',
                    description: 'Description for workflow routing agent',
                  })}
                </Text>
              </Layout>
            </Layout>
          </>
        )}
      </Layout>
    </Layout>
  );
};
